import { Service } from '@angular/core';
import { Translation, TranslocoLoader } from '@jsverse/transloco';

/**
 * Loads `/i18n/<lang>.json` — a same-origin static asset served by the web container, not an API
 * call. Uses raw `fetch()` rather than `HttpClient` deliberately: `shared-i18n` is `type:util`
 * (module-boundary constrained to depend only on other `type:util` libs), so it cannot reach the
 * `SKIP_INTERCEPTOR` token that lives in `core-data` (`type:data`) to opt an `HttpClient` request
 * out of the Cognito/territory interceptors. Going through `HttpClient` here would attach the
 * Bearer token and `X-Territory-Id` header to a static-file request that needs neither, and would
 * route a 401 on a translation file into the auth refresh/logout branch. `fetch()` never enters the
 * interceptor chain, so this is the smaller fix — see the identical justification for the one other
 * raw `fetch(` in the tree, `apps/vantage/src/app/app.config.ts` (`/config.json`).
 */
@Service()
export class TranslocoHttpLoader implements TranslocoLoader {
  async getTranslation(lang: string): Promise<Translation> {
    const res = await fetch(`/i18n/${lang}.json`);
    // `fetch` only rejects on a NETWORK failure — a 404 from the web container, or a CDN error page
    // served with a 200-adjacent status, resolves normally. Without this branch `res.json()` either
    // throws an opaque `Unexpected token '<'` (an HTML error page) or, worse, parses a non-dictionary
    // JSON body that Transloco then installs as the app's translations, so every translate call
    // across all three portals renders its own raw key. Name the lang and the status so the
    // bootstrap failure says which file and why.
    if (!res.ok) {
      throw new Error(`Failed to load /i18n/${lang}.json — HTTP ${res.status} ${res.statusText}`.trim());
    }
    const body: unknown = await res.json();
    // A dictionary is an object. An array, a string or `null` parses fine and is not one, and
    // Transloco does not check — it would accept the value and every key would miss.
    if (body === null || typeof body !== 'object' || Array.isArray(body)) {
      throw new Error(`Malformed /i18n/${lang}.json — expected a translation object, got ${Array.isArray(body) ? 'an array' : typeof body}`);
    }
    return body as Translation;
  }
}
